import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { FaCog, FaFileExcel } from 'react-icons/fa';

import SimplePanel from '@/shared/components/SimplePanel';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import intlShape from '@/shared/utils/intlShape';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import enumCache from '@/shared/services/EnumCacheService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import api from '@/shared/api';
import config from '@/config';
import { showWhenPermission } from '@/shared/components/security';
import Button from '@/shared/components/Button';
import handleHttpError from '@/shared/services/HandleHttpError';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';
import DataCacheService from '@/shared/services/DataCacheService';
import userStorage from '@/shared/services/userStorage';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import ScrollArrow from '@/shared/components/ScrollArrow';
import RelationRentabilityFilters, { defaultFilters } from './RelationRentabilityFilters';
import RelationRentabilityVirtualTable from './RelationRentabilityVirtualTable';
import messages from '../reportingMessages';
import { functionalities, privileges } from '@/shared/services/AuthService';

const SessionStorageKey = 'RelationRentabilityReportParams';
const columnSelectionStorageKey = 'RelationRentabilityReportHiddenColumns';

const totalProperties = [
  'registeredHours',
  'registeredHoursAmount',
  'billedHours',
  'billedHoursAmount',
  'inForfaitHours',
  'inForfaitHoursAmount',
  'addedHours',
  'addedHoursAmount',
  'removedHours',
  'removedHoursAmount',
  'registeredCosts',
  'billedCosts',
  'fixedAmounts',
  'freeLines',
  'writtenOffAmount',
  'turnover',
  'internalCost',
  'profit',
  'turnoverMinRegistered',
  'openRegisteredHours',
  'openRegisteredHoursAmount',
  'openRegisteredCosts',
  'neverInvoiceRegisteredHours',
  'neverInvoiceRegisteredHoursAmount',
  'neverInvoiceRegisteredCosts',
];

const calculateTotals = rows =>
  rows.reduce((totals, row) => {
    totalProperties.forEach(prop => {
      // eslint-disable-next-line no-param-reassign
      totals[prop] = (totals[prop] || 0) + (row[prop] || 0);
    });
    return totals;
  }, {});

// Init prop= visible on init
const columnDefinitions = [
  { id: 'sector', message: messages.Sector, init: true },
  { id: 'invoicingType', message: messages.InvoicingType, init: true },
  { id: 'registeredHours', message: messages.RegisteredHours, init: true },
  { id: 'billedHours', message: messages.BilledHours, init: true },
  { id: 'addedHours', message: messages.AddedHours, init: false },
  { id: 'removedHours', message: messages.RemovedHours, init: false },
  { id: 'inForfaitHours', message: messages.InForfait, init: true },
  { id: 'registeredCosts', message: messages.RegisteredCosts, init: true },
  { id: 'billedCosts', message: messages.BilledCosts, init: true },
  { id: 'fixedAmounts', message: messages.FixedAmountOrAdvance, init: true },
  { id: 'freeLines', message: messages.FreeLines, init: true },
  { id: 'turnover', message: messages.Turnover, init: true },
  { id: 'internalCost', message: messages.InternalCost, init: false },
  { id: 'profit', message: messages.Profit, init: false },
  { id: 'turnoverMinRegistered', message: messages.Rentability, init: true },
  { id: 'openRegisteredHours', message: messages.OpenRegisteredHours, init: true },
  { id: 'openRegisteredCosts', message: messages.OpenRegisteredCosts, init: false },
  { id: 'neverInvoiceRegisteredHours', message: messages.NeverInvoiceRegisteredHours, init: false },
  { id: 'neverInvoiceRegisteredCosts', message: messages.NeverInvoiceRegisteredCosts, init: false },
  { id: 'writtenOffAmount', message: messages.WrittenOffAmount, init: false },
];

class RelationRentabilityContainer extends Component {
  state = {
    reportFilters: {},
    rows: null,
    showHours: false,
    selectedRelationId: null,
    sectors: {},
    invoicingtypes: {},
  };

  constructor(props) {
    super(props);
    const { searchParams = {}, intl } = props;
    const sessionParams = JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    if (sessionParams.dateFrom) {
      sessionParams.dateFrom = moment(sessionParams.dateFrom);
    }
    if (sessionParams.dateUntil) {
      sessionParams.dateUntil = moment(sessionParams.dateUntil);
    }
    const columnsFromStorage = userStorage.getItem(columnSelectionStorageKey);
    const initColumns = typeof columnsFromStorage === 'undefined' || columnsFromStorage === null;

    const hiddenColumns = columnsFromStorage || [];
    const hideableColumns = columnDefinitions.map(({ id, message, init = true }) => ({
      id,
      label: intl.formatMessage(message),
      selected: !initColumns ? hiddenColumns.indexOf(id) === -1 : !!init,
    }));
    const columnStatus = hideableColumns.reduce((o, col) => {
      // eslint-disable-next-line no-param-reassign
      o[col.id] = !!col.selected;
      return o;
    }, {});

    this.state = {
      ...this.state,
      reportFilters: {
        ...defaultFilters,
        ...sessionParams,
        ...searchParams,
      },
      hideableColumns,
      columnStatus,
      showColumnSelectionModal: false,
      showHours: !!sessionStorage.getItem('RelationRentabilityReportShowHours'),
    };
  }

  componentDidMount() {
    const { intl } = this.props;

    this.assignmentsEnabled = DataCacheService.assignmentsEnabled();
    this.retrieve();

    enumCache.get('invoicingtype').then(invoicingtypes => {
      this.setState({
        invoicingtypes: invoicingtypes.reduce(
          (o, item) => ({
            ...o,
            [item.key]: intl.formatMessage(listItemMsg('invoicingtype', item.key, item.value)),
          }),
          {},
        ),
      });
    });

    enumCache.get('sector').then(sectors => {
      this.setState({
        sectors: sectors.reduce(
          (o, item) => ({
            ...o,
            [item.key]: intl.formatMessage(listItemMsg('sector', item.key, item.value)),
          }),
          {},
        ),
      });
    });
  }

  componentWillUnmount() {
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to leaving page.');
    }
  }

  getParams() {
    const { reportFilters } = this.state;
    const {
      relationId,
      dateFrom: from,
      dateUntil: until,
      relationTypes,
      invoicingType,
      ownCompanies,
      legalEntities,
      responsible,
      assistant,
      assignmentTemplateId,
      invoiceSchedule,
      withLabels,
      withoutLabels,
      teamCodes,
      associate,
      assistantBackup,
      billingUser,
      seniorUser,
      includeInactive,
      filterBehaviours,
      relationGroups,
      sectors,
      referenceYear,
    } = reportFilters;

    return {
      relationId,
      from,
      until,
      relationTypes,
      invoicingType,
      ownCompanies,
      legalEntities,
      responsible,
      assistant,
      assignmentTemplateId,
      invoiceSchedule,
      withLabels,
      withoutLabels,
      teamCodes,
      associate,
      assistantBackup,
      billingUser,
      seniorUser,
      includeInactive,
      filterBehaviours,
      relationGroups,
      sectors,
      referenceYear,
    };
  }

  retrieve() {
    const { reportFilters } = this.state;

    const params = this.getParams();

    const sessionParams = JSON.stringify(reportFilters);
    sessionStorage.setItem(SessionStorageKey, sessionParams);

    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to new request.');
    }
    this.reportToken = Axios.CancelToken.source();
    this.setState({ rows: null });
    return api.base
      .post(`${config.appUrls.reporting}/api/relationrentability`, params, {
        cancelToken: this.reportToken.token,
      })
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          rows: data,
        }));
        return data;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  }

  createExcelReport = () => {
    const { intl } = this.props;

    const reportParams = this.getParams();
    const languageCode = intl.locale;

    const params = {
      language: languageCode,
      signalRConnectionId: SignalRService.getConnectionId(),
      report: 1,
      parameters: JSON.stringify(reportParams),
    };

    return api.base
      .post(`${config.appUrls.reporting}/api/export`, params)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  filtersChanged(filters) {
    this.setState(
      state => ({
        ...state,
        reportFilters: {
          ...state.reportFilters,
          ...filters,
        },
      }),
      () => this.retrieve(),
    );
  }

  handleColumnSelection = items => {
    let hiddenColumns = items.filter(x => !x.selected).map(x => x.id);
    // Clear selection if all are hidden or none are hidden
    if (hiddenColumns.length === items.length || hiddenColumns.length === 0) hiddenColumns = [];
    userStorage.setItem(columnSelectionStorageKey, hiddenColumns);

    const { hideableColumns } = this.state;

    const newHideableColumns = hideableColumns.map(x => ({
      ...x,
      selected: hiddenColumns.indexOf(x.id) === -1,
    }));
    const columnStatus = newHideableColumns.reduce((o, col) => {
      // eslint-disable-next-line no-param-reassign
      o[col.id] = !!col.selected;
      return o;
    }, {});

    this.setState({
      hideableColumns: newHideableColumns,
      columnStatus,
      showColumnSelectionModal: false,
    });
  };

  showHoursChanged = value => {
    sessionStorage.setItem('RelationRentabilityReportShowHours', value);
    this.setState({ showHours: value });
  };

  handleRowClick = relationId => {
    this.setState({ selectedRelationId: relationId });
  };

  render() {
    const { intl } = this.props;
    const {
      reportFilters,
      rows,
      showHours,
      showColumnSelectionModal,
      columnStatus,
      hideableColumns,
      selectedRelationId,
      sectors,
      invoicingtypes,
    } = this.state;
    const showAssignments = this.assignmentsEnabled && !!rows && rows.some(x => !!x.assignmentId);
    const totals = rows ? calculateTotals(rows) : {};

    return (
      <div className="relation-rentability">
        <SimplePanel
          article="5434879956242"
          title={intl.formatMessage(messages.RelationRentability)}
        >
          <div className="info-block container-fluid">
            <RelationRentabilityFilters
              filters={reportFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
              showHours={showHours}
              onShowHoursChanged={this.showHoursChanged}
            />
          </div>
        </SimplePanel>
        {showColumnSelectionModal ? (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectColumns)}
            items={hideableColumns}
            onSave={this.handleColumnSelection}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        ) : null}
        {rows ? (
          <>
            <div className="row btn-row">
              <div className="col-md-6 pull-right content-right">
                <Button
                  btnClassName="btn btn-xs btn-default margin10R"
                  onClick={() => this.setState({ showColumnSelectionModal: true })}
                >
                  <span className="margin5R">
                    <FaCog className="react-icon" />
                  </span>
                  <FormattedMessage {...messages.ColumnConfig} />
                </Button>
                <Button
                  btnClassName="btn btn-xs btn-default margin10R"
                  onClick={() => this.createExcelReport()}
                  showProgress
                >
                  <span className="margin5R">
                    <FaFileExcel className="file-icon" />
                  </span>
                  {intl.formatMessage(messages.ExportToExcel)}
                </Button>
              </div>
            </div>
            <RelationRentabilityVirtualTable
              data={rows}
              columnStatus={columnStatus}
              showAssignments={showAssignments}
              showHours={showHours}
              totals={totals}
              reportFilters={reportFilters}
              sectors={sectors}
              invoicingtypes={invoicingtypes}
              selectedRelationId={selectedRelationId}
              onRowClick={this.handleRowClick}
            />
            <ScrollArrow />
          </>
        ) : (
          <LoadingAnimation />
        )}
      </div>
    );
  }
}

RelationRentabilityContainer.propTypes = {
  intl: intlShape.isRequired,
  searchParams: PropTypes.shape({ relationId: PropTypes.string }),
};

RelationRentabilityContainer.defaultProps = {
  searchParams: {},
};

export default showWhenPermission(
  functionalities.RentabilityReport,
  privileges.Read,
)(injectIntl(RelationRentabilityContainer));
